import React, { useState, useEffect } from 'react';
import { Modal } from '../basic';
import { shareService } from '../../services/shareService';
import { toast } from 'react-toastify';

interface Game {
  _id: string;
  name?: string;
  opponentName?: string;
  date?: string;
}

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: 'season' | 'game';
  resourceId: string;
  availableGames?: Game[];
  currentGameId?: string;
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  contentType,
  resourceId,
  availableGames = [],
  currentGameId,
}) => {
  const [shareScope, setShareScope] = useState<'entire-season' | 'specific-game'>('entire-season');
  const [selectedGameId, setSelectedGameId] = useState<string>(currentGameId || '');
  const [shareType, setShareType] = useState<'teams' | 'players'>('teams');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Initialize modal state based on content type
  useEffect(() => {
    if (contentType === 'game') {
      setShareScope('specific-game');
      setSelectedGameId(resourceId);
    } else {
      setShareScope('entire-season');
    }
  }, [contentType, resourceId]);

  // Set default game for season sharing when games become available
  useEffect(() => {
    if (contentType === 'season' && availableGames.length > 0 && !selectedGameId) {
      setSelectedGameId(availableGames[0]._id);
    }
  }, [availableGames, contentType, selectedGameId]);

  useEffect(() => {
    if (isOpen) {
      setGeneratedLink('');
      setShowSuccess(false);
    }
  }, [isOpen]);

  const handleGenerateShare = async () => {
    setIsGenerating(true);
    try {
      const finalContentType = shareScope === 'entire-season' ? 'season' : 'game';
      const finalResourceId = shareScope === 'entire-season' ? resourceId : selectedGameId;

      const result = await shareService.generateShareLink(finalContentType, finalResourceId, shareType);

      setGeneratedLink(result.shareUrl);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate share link. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyAndShare = async () => {
    if (!generatedLink) return;

    const contentTitle =
      shareScope === 'entire-season'
        ? `Season ${shareType === 'teams' ? 'Teams Analytics' : 'Players Report'}`
        : `Game ${shareType === 'teams' ? 'Teams Report' : 'Players Report'}`;

    const result = await shareService.handleShare(generatedLink, contentTitle);

    if (result.success) {
      if (result.method === 'native') {
        onClose();
      } else {
        toast.success('Link copied to clipboard!');
      }
    } else {
      toast.error('Failed to copy link. Please copy manually.');
    }
  };

  const selectedGame = availableGames.find((game) => game._id === selectedGameId);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-full max-w-xl p-6 mx-auto">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-[#19133c] mb-2">Share Content</h2>
          <p className="text-gray-600">Generate a shareable link for your statistics</p>
        </div>

        {!showSuccess ? (
          <div className="space-y-6">
            {/* Share Scope Selection */}
            {contentType === 'season' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">What would you like to share?</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="shareScope"
                      value="entire-season"
                      checked={shareScope === 'entire-season'}
                      onChange={(e) => setShareScope(e.target.value as 'entire-season')}
                      className="mr-3"
                    />
                    <span>Entire Season</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="shareScope"
                      value="specific-game"
                      checked={shareScope === 'specific-game'}
                      onChange={(e) => setShareScope(e.target.value as 'specific-game')}
                      className="mr-3"
                    />
                    <span>Specific Game</span>
                  </label>
                </div>
              </div>
            )}

            {/* Game Selection */}
            {shareScope === 'specific-game' && availableGames.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Game</label>
                <select
                  value={selectedGameId}
                  onChange={(e) => setSelectedGameId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {availableGames.map((game) => (
                    <option key={game._id} value={game._id}>
                      {game.opponentName || game.name || `Game ${game._id.slice(-6)}`}
                      {game.date && ` - ${new Date(game.date).toLocaleDateString()}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Report Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Which report?</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setShareType('teams')}
                  className={`p-4 border-2 rounded-lg text-center transition-all ${
                    shareType === 'teams'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold">Teams Analytics</div>
                  <div className="text-sm text-gray-500">Team performance stats</div>
                </button>
                <button
                  type="button"
                  onClick={() => setShareType('players')}
                  className={`p-4 border-2 rounded-lg text-center transition-all ${
                    shareType === 'players'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold">Players Report</div>
                  <div className="text-sm text-gray-500">Individual player stats</div>
                </button>
              </div>
            </div>

            {/* Generate Button */}
            <div className="pt-4">
              <button
                onClick={handleGenerateShare}
                disabled={isGenerating || (shareScope === 'specific-game' && !selectedGameId)}
                className="w-full bg-[#05396b] text-white py-3 rounded-lg font-semibold hover:bg-[#04304d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? 'Generating...' : 'Generate Share Link'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Success State */}
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Share Link Generated!</h3>
              <p className="text-gray-600 mb-4">
                Share this link to give others access to your {shareScope === 'entire-season' ? 'season' : 'game'}{' '}
                {shareType === 'teams' ? 'teams analytics' : 'players report'}
                {shareScope === 'specific-game' && selectedGame && (
                  <span> for {selectedGame.opponentName || selectedGame.name}</span>
                )}
              </p>
            </div>

            {/* Generated Link */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-xs text-gray-500 mb-1">Share Link:</div>
              <div className="text-sm font-mono break-all text-gray-800">{generatedLink}</div>
            </div>

            {/* Action Buttons Row */}
            <div className="flex gap-3">
              <button
                onClick={handleCopyAndShare}
                className="flex-1 bg-[#22c55e] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#16a34a] transition-colors"
              >
                Copy & Share
              </button>
              <button
                onClick={() => setShowSuccess(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                Generate Another
              </button>
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="mt-8 text-center">
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors text-sm font-medium">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ShareModal;
